/** @jsxImportSource @emotion/react */

import Modal from "react-modal";
import UI from "@mile-meeting/mile-ui";
import { Title, MeetingRoom } from "pages/meeting/booking-list/reservation-modal/input-forms";
import { useEffect, useState } from "react";
import { getMeetingRoomList, TMeetingRoom } from "apis/meetings/meeting-room";
import DateInput from "./RequestRestrictionModal/DateInput";
import TimeInput from "./RequestRestrictionModal/TimeInput";

const modalStyle = {
  content: {
    top: "50%",
    left: "50%",
    right: "auto",
    bottom: "auto",
    marginRight: "-50%",
    border: "none",
    background: "transparent",
    transform: "translate(-50%, -50%)",
    width: 600,
  },
  overlay: {
    backgroundColor: "var(--component-color-material-dimmer)",
    zIndex: 1000,
  },
};
Modal.setAppElement("#root");

type Props = {
  modalIsOpen: boolean;
  closeModal: () => void;
  onRegister: (meetingRoom: TMeetingRoom, meetingTitle: string, startTime: Date, endTime: Date, reason: string) => void;
};

const RequestRestrictionModal = ({ modalIsOpen, closeModal, onRegister }: Props) => {
  const [meetingRoomList, setMeetingRoomList] = useState<TMeetingRoom[]>([]);
  const [selectedMeetingRoom, setSelectedMeetingRoom] = useState<TMeetingRoom | null>();
  const [meetingTitle, setMeetingTitle] = useState<string>("[회의실 예약 제한] ");
  const [selectedStartTime, setSelectedStartTime] = useState<Date>(new Date());
  const [selectedEndTime, setSelectedEndTime] = useState<Date>(new Date());
  const [reason, setReason] = useState<string | null>();
  const [maxEndDate, setMaxEndDate] = useState<Date>();

  const fetchMeetingRoomList = async () => {
    const res = await getMeetingRoomList(1, [], [], 50);
    if (res.success) {
      setMeetingRoomList(res.data?.meetingRoomList ?? []);
    }
  };

  const isActiveRegisterBtn = selectedMeetingRoom && meetingTitle && meetingTitle.length > 0 && reason && reason.length > 0 ? true : false;

  const setTimeFormatter = (
    date: Date,
    hour: number,
    minutes: number,
    timeSetter: (value: Date) => void,
    isStartTime: boolean
  ) => {
    const newTime = new Date(date);
    newTime.setHours(hour);
    newTime.setMinutes(minutes);

    timeSetter(newTime);

    // 만약 StartTime이 변경된 경우, endTime 보다 늦는지 확인
    if (isStartTime && newTime >= selectedEndTime) {
      const endTime = new Date(newTime);
      endTime.setMinutes(endTime.getMinutes() + 30);
      setSelectedEndTime(endTime);
    }

    // 만약 endTime이 변경된 경우, startTime 보다 빠른지 확인
    if (!isStartTime && newTime <= selectedStartTime) {
      const startTime = new Date(newTime);
      startTime.setMinutes(startTime.getMinutes() - 30);
      setSelectedStartTime(startTime);
    }

    // 만약 endTime이 startTime보다 1달 이후일 경우
    const startAfter30Days = new Date(newTime);
    startAfter30Days.setDate(startAfter30Days.getDate() + 31);
    if (isStartTime && startAfter30Days < selectedEndTime) {
      const endTime = new Date(newTime);
      endTime.setMinutes(endTime.getMinutes() + 30);
      setSelectedEndTime(endTime);
    }

    if (isStartTime) {
      const after30Days = new Date(newTime);
      after30Days.setDate(after30Days.getDate() + 30);
      setMaxEndDate(after30Days);
    }
  };

  const getInitialStartStartTime = () => {
    const now = new Date();
    const hours = now.getHours();
    const minutes = now.getMinutes();

    // 22:30 이상 or 07:00 이전 → 다음날 07:00
    if (hours < 7 || (hours === 22 && minutes >= 30) || hours > 22) {
      const nextDay = new Date(now);
      nextDay.setDate(now.getDate() + 1);
      nextDay.setHours(7, 0, 0, 0);
      return nextDay;
    }

    // 현재 시간을 기준으로 다음 30분 단위 계산
    const rounded = new Date(now);
    rounded.setSeconds(0, 0);

    if (minutes === 0 || minutes === 30) {
      // 이미 30분 단위면 다음 슬롯으로
      rounded.setMinutes(minutes + 30);
    } else if (minutes < 30) {
      rounded.setMinutes(30);
    } else {
      rounded.setMinutes(60);
    }

    return rounded;
  };

  const getInitialEndTime = () => {
    const initialStartTime = getInitialStartStartTime();

    const endTime = new Date(initialStartTime);
    endTime.setHours(endTime.getHours() + 1);

    return endTime;
  };

  useEffect(() => {
    if (modalIsOpen) {
      fetchMeetingRoomList();
      setSelectedMeetingRoom(null);
      setMeetingTitle("[회의실 예약 제한] ");
      setReason(null);
      setSelectedStartTime(getInitialStartStartTime());
      setSelectedEndTime(getInitialEndTime());
      const after30Days = new Date(getInitialStartStartTime());
      after30Days.setDate(after30Days.getDate() + 30);
      setMaxEndDate(after30Days);
    }
  }, [modalIsOpen]);

  return (
    <Modal isOpen={modalIsOpen} onRequestClose={closeModal} style={modalStyle}>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          padding: 40,
          gap: 20,
          background: "white",
          borderRadius: 16,
          position: "relative",
        }}
      >
        <div className="cancelBtn" style={{ position: "absolute", right: 20, top: 20 }}>
          <UI.IconButton.Normal icon="x" size={20} color="var(--alias-color-label-normal)" onClick={closeModal} />
        </div>
        <div
          className="title"
          style={{ display: "flex", paddingBottom: 20, borderBottom: "1px solid var(--alias-color-line-normal)" }}
        >
          <UI.Typography type="title03" fontStyle="bold">
            회의실 예약 제한 요청
          </UI.Typography>
        </div>
        <div className="form" style={{ display: "flex", flexDirection: "column", gap: 40 }}>
          <div className="form-top-section" style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            <div className="meetingRoom" style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <MeetingRoom
                meetingRooms={meetingRoomList}
                onChange={(value) => setSelectedMeetingRoom(value)}
                value={selectedMeetingRoom ?? undefined}
              />
            </div>
            <div className="meetingRoom" style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <Title
                onChange={(value) => setMeetingTitle(value)}
                value={meetingTitle ?? undefined}
              />
            </div>
            <div className="startTime" style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <div className="label" style={{ display: "flex", gap: 4 }}>
                <UI.Typography type="label01" fontStyle="bold" color="var(--alias-color-label-alternative)">
                  제한 시작 일시
                </UI.Typography>
                <UI.Typography type="label01" fontStyle="bold" color="var(--alias-color-primary-normal)">
                  *
                </UI.Typography>
              </div>
              <div className="settingTime" style={{ display: "flex", gap: 12 }}>
                <DateInput
                  value={selectedStartTime}
                  onChange={(value) =>
                    setTimeFormatter(
                      value,
                      selectedStartTime.getHours(),
                      selectedStartTime.getMinutes(),
                      setSelectedStartTime,
                      true
                    )
                  }
                />
                <TimeInput
                  onChange={(hour, minutes) => {
                    setTimeFormatter(selectedStartTime, hour, minutes, setSelectedStartTime, true);
                  }}
                  value={selectedStartTime}
                  maxTime={{ hour: 22, minutes: 30 }}
                />
              </div>
            </div>
            <div className="endTime" style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <div className="label" style={{ display: "flex", gap: 4 }}>
                <UI.Typography type="label01" fontStyle="bold" color="var(--alias-color-label-alternative)">
                  제한 종료 일시
                </UI.Typography>
                <UI.Typography type="label01" fontStyle="bold" color="var(--alias-color-primary-normal)">
                  *
                </UI.Typography>
              </div>
              <div className="settingTime" style={{ display: "flex", gap: 12 }}>
                <DateInput
                  value={selectedEndTime}
                  maxDate={maxEndDate}
                  onChange={(value) =>
                    setTimeFormatter(
                      value,
                      selectedEndTime.getHours(),
                      selectedEndTime.getMinutes(),
                      setSelectedEndTime,
                      false
                    )
                  }
                />
                <TimeInput
                  onChange={(hour, minutes) => {
                    setTimeFormatter(selectedEndTime, hour, minutes, setSelectedEndTime, false);
                  }}
                  value={selectedEndTime}
                  minTime={{ hour: 7, minutes: 30 }}
                  maxTime={{ hour: 23, minutes: 0 }}
                />
              </div>
            </div>
            <div className="reason" style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <div className="label" style={{ display: "flex", gap: 4 }}>
                <UI.Typography type="label01" fontStyle="bold" color="var(--alias-color-label-alternative)">
                  예약 제한 사유
                </UI.Typography>
                <UI.Typography type="label01" fontStyle="bold" color="var(--alias-color-primary-normal)">
                  *
                </UI.Typography>
              </div>
              <UI.Input
                multiline
                height={98}
                placeholder="회의실 예약 제한 사유"
                width={"100%"}
                showMaxLength
                maxLength={100}
                value={reason ?? ""}
                onChange={(value) => {
                  if (value.length > 100) return;
                  setReason(value);
                }}
              />
            </div>
          </div>
          <UI.Button
            title="등록하기"
            disabled={!isActiveRegisterBtn}
            size="large"
            onClick={() => {
              if (selectedMeetingRoom && reason) {
                onRegister(selectedMeetingRoom, meetingTitle, selectedStartTime, selectedEndTime, reason);
              }
            }}
          />
        </div>
      </div>
    </Modal>
  );
};

export default RequestRestrictionModal;
